import { buildEntryUrl } from "@/react/helpers/entryUrlHelpers";

// One-shot entry params for the member Avatars panel, consumed then stripped
// like the CircleAI ones in `CircleAi/intents/urlParams.ts`.
//
//   ?avatar=new&avatar_id=<id>&avatar_view_type=full_screen
//                                                blank conversation with that avatar, full width
//   ?avatar_chat_id=<id>                         conversation, docked
export const AVATAR_PARAM = "avatar";
export const AVATAR_ID_PARAM = "avatar_id";
export const AVATAR_CHAT_ID_PARAM = "avatar_chat_id";
export const AVATAR_VIEW_TYPE_PARAM = "avatar_view_type";

export interface AvatarPanelSearchParams {
  [key: string]: unknown;
  [AVATAR_CHAT_ID_PARAM]?: string | number;
  [AVATAR_ID_PARAM]?: string | number;
  [AVATAR_PARAM]?: string;
  [AVATAR_VIEW_TYPE_PARAM]?: string;
}

/**
 * Absolute URL that opens the Avatars panel, fullscreen or docked, on the page
 * the user is currently on. A `chatId` reopens that conversation; `null`
 * starts a blank one with `avatarId`.
 */
export const buildAvatarPanelEntryUrl = (
  chatId: number | null,
  isFullscreen: boolean,
  avatarId?: number,
) => {
  const isNewConversation = chatId === null;

  return buildEntryUrl({
    [AVATAR_PARAM]: isNewConversation ? "new" : null,
    [AVATAR_ID_PARAM]:
      isNewConversation && avatarId !== undefined ? String(avatarId) : null,
    [AVATAR_CHAT_ID_PARAM]: isNewConversation ? null : String(chatId),
    [AVATAR_VIEW_TYPE_PARAM]: isFullscreen ? "full_screen" : "panel",
  });
};

/**
 * Absolute URL members can open to land on the feed with the docked Avatars
 * panel on a blank conversation with `avatarId`. Unlike the entry URLs above it
 * does not depend on the page the admin is on, which members may not reach.
 */
export const buildAvatarShareUrl = (avatarId: number) =>
  buildEntryUrl(
    {
      [AVATAR_PARAM]: "new",
      [AVATAR_ID_PARAM]: String(avatarId),
      [AVATAR_VIEW_TYPE_PARAM]: "panel",
    },
    { origin: window.location.origin, pathname: "/feed", search: "" },
  );

const parsePositiveInteger = (value: unknown) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

// The router JSON-parses search values, so numeric ids arrive as numbers.
// `chatId: null` means a blank conversation, with `avatarId` as its avatar.
export const parseAvatarPanelUrlEntry = (search: AvatarPanelSearchParams) => {
  const chatId = parsePositiveInteger(search[AVATAR_CHAT_ID_PARAM]);

  if (chatId === null && search[AVATAR_PARAM] !== "new") return null;

  return {
    chatId,
    avatarId:
      chatId === null ? parsePositiveInteger(search[AVATAR_ID_PARAM]) : null,
    view:
      search[AVATAR_VIEW_TYPE_PARAM] === "full_screen"
        ? "fullscreen"
        : "default",
  } as const;
};
